"use client";

import { Check, Copy, Zap, Code } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { formatContextSize } from "@/lib/utils";

import { PRICING_CONFIG } from "@routerbench/shared";
import { getProviderIcon } from "@routerbench/shared/components";

import { useCurrency } from "./use-currency";

import type {
	ProviderDefinition,
	ProviderModelMapping,
	StabilityLevel,
} from "@routerbench/models";

interface ProviderWithInfo extends ProviderModelMapping {
	providerInfo?: ProviderDefinition;
}

interface AvailableProvidersTableProps {
	providers: ProviderWithInfo[];
	modelName: string;
	playgroundUrl: string;
	modelStability?: StabilityLevel;
	isEmbedding?: boolean;
	isAudio?: boolean;
	isVideo?: boolean;
	isMusic?: boolean;
}

type CodeTab = "openai" | "client" | "curl";

export function AvailableProvidersTable({
	providers,
	modelName,
	playgroundUrl,
	modelStability,
	isEmbedding,
	isAudio,
	isVideo,
	isMusic,
}: AvailableProvidersTableProps) {
	const [copiedModel, setCopiedModel] = useState<string | null>(null);
	const [showCodeDialog, setShowCodeDialog] = useState<string | null>(null);
	const [copiedCode, setCopiedCode] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<CodeTab>("openai");
	const [currency] = useCurrency();

	const currencySymbol = currency === "usd" ? "$" : "$";
	const rate = currency === "usd" ? 1 : PRICING_CONFIG.usdToInrRate;

	const formatPrice = (price: number | undefined) => {
		if (price === undefined) {
			return "—";
		}
		return `${currencySymbol}${(price * 1e6 * rate).toFixed(2)}`;
	};

	const getStatusBadge = (providerStability?: StabilityLevel) => {
		// Preview / experimental models aren't "degraded" — they're just new.
		if (providerStability === "experimental" || providerStability === "beta") {
			return {
				icon: "🔵",
				label: "Preview",
				className: "bg-blue-500/10 text-blue-600 border-blue-500/20",
			};
		}
		if (providerStability === "unstable") {
			return {
				icon: "🟡",
				label: "Degraded",
				className:
					"bg-status-warn-bg text-status-warn-title border-status-warn-border",
			};
		}
		return {
			icon: "🟢",
			label: "Operational",
			className: "bg-status-ok-bg text-status-ok-title border-status-ok-border",
		};
	};

	// Video models are priced per second of output, not per token.
	const videoPerSecLabel = (provider: ProviderWithInfo) => {
		const tiers = provider.videoPricingTiers;
		const per =
			tiers && tiers.length > 0
				? Math.min(...tiers.map((t) => t.pricePerSecond))
				: provider.videoPricePerSecond;
		return per !== undefined
			? `${currencySymbol}${(per * rate).toFixed(2)}/s`
			: "—";
	};

	// Music models are priced per generation (flat), not per token.
	const musicPerSongLabel = (provider: ProviderWithInfo) => {
		const per = provider.audioMusicPricePerGeneration;
		return per !== undefined
			? `${currencySymbol}${(per * rate).toFixed(2)}/song`
			: "—";
	};

	// Calculate cheapest provider
	const cheapestProvider = providers.reduce((cheapest, current) => {
		if (!current.inputPrice) {
			return cheapest;
		}
		if (!cheapest.inputPrice) {
			return current;
		}

		const currentPrice = current.inputPrice * (1 - (current.discount ?? 0));
		const cheapestPrice = cheapest.inputPrice * (1 - (cheapest.discount ?? 0));

		return currentPrice < cheapestPrice ? current : cheapest;
	});

	const isCheapest = (provider: ProviderWithInfo) => {
		if (!provider.inputPrice || !cheapestProvider.inputPrice) {
			return false;
		}
		const providerPrice = provider.inputPrice * (1 - (provider.discount ?? 0));
		const cheapestPrice =
			cheapestProvider.inputPrice * (1 - (cheapestProvider.discount ?? 0));
		return Math.abs(providerPrice - cheapestPrice) < 0.0001;
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedModel(text);
			setTimeout(() => setCopiedModel(null), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const copyCodeToClipboard = async (code: string, codeType: string) => {
		try {
			await navigator.clipboard.writeText(code);
			setCopiedCode(codeType);
			setTimeout(() => setCopiedCode(null), 2000);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};

	const getCodeExamples = (providerModelName: string) => {
		const modelName = providerModelName.split("/")[1];

		if (isAudio) {
			const openaiSdkCode = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.ROUTERBENCH_API_KEY,
  baseURL: "https://api.routerbench.com/v1/"
});

// Text-to-Speech
const audio = await client.audio.speech.create({
  model: "${modelName}",
  input: "Hello, welcome to client!",
  voice: "alloy"
});

const buffer = Buffer.from(await audio.arrayBuffer());
require("fs").writeFileSync("output.mp3", buffer);`;

			const routerbenchSdkCode = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.ROUTERBENCH_API_KEY,
  baseURL: "https://api.routerbench.com/v1/"
});

// Speech-to-Text
const transcription = await client.audio.transcriptions.create({
  model: "${modelName}",
  file: require("fs").createReadStream("audio.mp3")
});

console.log(transcription.text);`;

			const curlCode = `# Text-to-Speech
curl -X POST https://api.routerbench.com/v1/audio/speech \\
  -H "Authorization: Bearer $ROUTERBENCH_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "${modelName}", "input": "Hello!", "voice": "alloy"}' \\
  --output speech.mp3

# Speech-to-Text
curl -X POST https://api.routerbench.com/v1/audio/transcriptions \\
  -H "Authorization: Bearer $ROUTERBENCH_API_KEY" \\
  -F file=@audio.mp3 \\
  -F model=${modelName}`;

			return {
				openaiSdk: openaiSdkCode,
				routerbenchSdk: routerbenchSdkCode,
				curl: curlCode,
			};
		}

		if (isMusic) {
			const openaiSdkCode = `import fs from "fs";

const r = await fetch("https://api.routerbench.com/v1/music", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": \`Bearer \${process.env.ROUTERBENCH_API_KEY}\`,
  },
  body: JSON.stringify({
    model: "${modelName}",
    prompt: "Upbeat lo-fi hip hop, 85 BPM, instrumental",
  }),
});
const data = await r.json();
fs.writeFileSync("song.mp3", Buffer.from(data.audio.base64, "base64"));`;

			const routerbenchSdkCode = `import fs from "fs";

// Lyria Pro accepts WAV output and reference images.
const r = await fetch("https://api.routerbench.com/v1/music", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": \`Bearer \${process.env.ROUTERBENCH_API_KEY}\`,
  },
  body: JSON.stringify({
    model: "${modelName}",
    prompt: "Cinematic strings building to a triumphant brass finale",
    response_format: "wav",
    instrumental: true,
  }),
});
const data = await r.json();
console.log("Lyrics / structure:", data.text);
fs.writeFileSync("song.wav", Buffer.from(data.audio.base64, "base64"));`;

			const curlCode = `curl -X POST https://api.routerbench.com/v1/music \\
  -H "Authorization: Bearer $ROUTERBENCH_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelName}",
    "prompt": "Upbeat lo-fi hip hop, 85 BPM, instrumental"
  }' | jq -r '.audio.base64' | base64 -d > song.mp3`;

			return {
				openaiSdk: openaiSdkCode,
				routerbenchSdk: routerbenchSdkCode,
				curl: curlCode,
			};
		}

		if (isEmbedding) {
			const openaiSdkCode = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.ROUTERBENCH_API_KEY,
  baseURL: "https://api.routerbench.com/v1/"
});

const response = await client.embeddings.create({
  model: "${modelName}",
  input: "The quick brown fox jumps over the lazy dog"
});

console.log(response.data[0].embedding.slice(0, 5));`;

			const routerbenchSdkCode = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.ROUTERBENCH_API_KEY,
  baseURL: "https://api.routerbench.com/v1/"
});

// Batch embedding
const response = await client.embeddings.create({
  model: "${modelName}",
  input: [
    "First document to embed",
    "Second document to embed"
  ]
});

response.data.forEach((item, i) =>
  console.log(\`Embedding \${i}: \${item.embedding.length} dimensions\`)
);`;

			const curlCode = `curl -X POST https://api.routerbench.com/v1/embeddings \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $ROUTERBENCH_API_KEY" \\
  -d '{
  "model": "${modelName}",
  "input": "The quick brown fox jumps over the lazy dog"
}'`;

			return {
				openaiSdk: openaiSdkCode,
				routerbenchSdk: routerbenchSdkCode,
				curl: curlCode,
			};
		}

		const openaiSdkCode = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.ROUTERBENCH_API_KEY, // or your API key string
  baseURL: "https://api.routerbench.com/v1/"
});

const response = await client.chat.completions.create({
  model: "${modelName}",
  messages: [
    { role: "user", content: "Hello, how are you?" }
  ]
});

console.log(response.choices[0].message.content);`;

		const routerbenchSdkCode = `import { createRouterbench } from "@routerbench/ai-sdk-provider";
import { generateText } from "ai";

const client = createRouterbench({ apiKey: process.env.ROUTERBENCH_API_KEY });

const { text } = await generateText({
  model: client("${modelName}"),
  prompt: "Write a vegetarian lasagna recipe for 4 people.",
});

console.log(text);`;

		const curlCode = `curl -X POST https://api.routerbench.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $ROUTERBENCH_API_KEY" \\
  -d '{
  "model": "${modelName}",
  "messages": [
    {"role": "user", "content": "Hello, how are you?"}
  ]
}'`;

		return {
			openaiSdk: openaiSdkCode,
			routerbenchSdk: routerbenchSdkCode,
			curl: curlCode,
		};
	};

	return (
		<div>
			<div className="mb-6">
				<div className="flex items-start justify-between gap-4">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
							Available Providers
						</h2>
						<p className="text-gray-600 dark:text-slate-400">
							client automatically routes requests to the optimal provider based
							on your prompt size and specified parameters.
						</p>
					</div>
				</div>
			</div>

			{/* Desktop Table View */}
			<div className="hidden md:block border border-gray-200 dark:border-gray-800 rounded-lg overflow-x-auto shadow-sm">
				<table className="w-full" style={{ tableLayout: "fixed" }}>
					<thead>
						<tr className="bg-gray-50 dark:bg-gray-900/50 border-b-2 border-gray-300 dark:border-slate-700">
							<th
								className="font-semibold text-gray-900 dark:text-white text-left px-4 py-3"
								style={{ width: "22%" }}
							>
								Provider
							</th>
							<th
								className="font-semibold text-gray-900 dark:text-white text-center px-4 py-3"
								style={{ width: "10%" }}
							>
								Context
							</th>
							<th
								className="font-semibold text-gray-900 dark:text-white text-right px-4 py-3"
								style={{ width: "13%" }}
							>
								Input Price
							</th>
							<th
								className="font-semibold text-gray-900 dark:text-white text-right px-4 py-3"
								style={{ width: "13%" }}
							>
								Output Price
							</th>
							<th
								className="font-semibold text-gray-900 dark:text-white text-center px-4 py-3"
								style={{ width: "12%" }}
							>
								Status
							</th>
							{!isEmbedding && !isAudio && !isMusic && (
								<th
									className="font-semibold text-gray-900 dark:text-white text-center px-4 py-3"
									style={{ width: "12%" }}
								>
									Quick Start
								</th>
							)}
							<th
								className="font-semibold text-gray-900 dark:text-white text-center px-4 py-3"
								style={{ width: "8%" }}
							>
								Code
							</th>
							<th
								className="font-semibold text-gray-900 dark:text-white text-center px-4 py-3"
								style={{ width: "10%" }}
							>
								Copy
							</th>
						</tr>
					</thead>
					<tbody>
						{providers.map((provider) => {
							const providerStability = provider.stability || modelStability;
							const statusBadge = getStatusBadge(providerStability);
							const ProviderIcon = getProviderIcon(provider.providerId);
							const providerModelName = `${provider.providerId}/${modelName}`;

							return (
								<tr
									key={`${provider.providerId}-${provider.modelName}-${modelName}`}
									className="border-b border-gray-200 dark:border-gray-800 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors"
								>
									{/* Provider Name */}
									<td className="font-medium text-gray-900 dark:text-white text-left px-4 py-3">
										<div className="flex items-center gap-2 min-w-0">
											<div className="w-8 h-8 bg-gray-100 dark:bg-slate-800 rounded flex items-center justify-center flex-shrink-0">
												{ProviderIcon ? (
													<ProviderIcon className="h-6 w-6" />
												) : (
													<span className="text-xs font-semibold">
														{provider.providerInfo?.name
															?.charAt(0)
															.toUpperCase() || "?"}
													</span>
												)}
											</div>
											<div className="truncate min-w-0 flex-1">
												<div className="flex items-center gap-2 min-w-0">
													<div className="font-semibold truncate">
														{provider.providerInfo?.name || provider.providerId}
													</div>
													{isCheapest(provider) && (
														<Badge
															variant="outline"
															className="text-xs bg-green-500/10 text-green-600 border-green-500/20 dark:text-green-400 whitespace-nowrap"
														>
															Best
														</Badge>
													)}
												</div>
												<code className="text-xs text-gray-500 dark:text-slate-400 font-mono truncate block">
													{providerModelName}
												</code>
											</div>
										</div>
									</td>

									{/* Context Window */}
									<td className="text-gray-900 dark:text-white text-center px-4 py-3">
										{provider.contextSize
											? formatContextSize(provider.contextSize)
											: "—"}
									</td>

									{/* Input Price */}
									<td
										className={`font-mono text-right text-sm font-semibold px-4 py-3 ${
											isCheapest(provider)
												? "bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300"
												: "text-gray-900 dark:text-white"
										}`}
									>
										{isVideo ? (
											videoPerSecLabel(provider)
										) : isMusic ? (
											musicPerSongLabel(provider)
										) : provider.discount ? (
											<div className="flex flex-col items-end">
												<span className="line-through text-gray-400 text-xs">
													{formatPrice(provider.inputPrice)}
												</span>
												<div>
													{formatPrice(
														provider.inputPrice!
															? provider.inputPrice * (1 - provider.discount)
															: undefined,
													)}
												</div>
											</div>
										) : (
											formatPrice(provider.inputPrice)
										)}
									</td>

									{/* Output Price */}
									<td className="text-gray-900 dark:text-white font-mono text-right px-4 py-3">
										{isVideo || isMusic
											? "—"
											: formatPrice(provider.outputPrice)}
									</td>

									{/* Status */}
									<td className="text-center px-4 py-3">
										<div className="flex justify-center">
											<Badge
												variant="outline"
												className={`text-xs ${statusBadge.className}`}
											>
												{statusBadge.label}
											</Badge>
										</div>
									</td>

									{/* Quick Start Button — hidden for embedding/audio/music models */}
									{!isEmbedding && !isAudio && !isMusic && (
										<td className="text-center px-4 py-3">
											<Button
												asChild
												size="sm"
												className="h-8 gap-1 bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white"
											>
												<a
													href={`${playgroundUrl}?model=${encodeURIComponent(
														providerModelName,
													)}`}
													target="_blank"
													rel="noopener noreferrer"
												>
													<Zap className="h-3.5 w-3.5" />
													<span className="hidden sm:inline text-xs font-medium">
														Start
													</span>
												</a>
											</Button>
										</td>
									)}

									{/* Code Button */}
									<td className="text-center px-4 py-3">
										<Button
											size="sm"
											variant="ghost"
											className="h-8 w-8 p-0"
											onClick={() => setShowCodeDialog(providerModelName)}
											title="View code examples"
										>
											<Code className="h-4 w-4" />
										</Button>
									</td>

									{/* Copy Button */}
									<td className="text-center px-4 py-3">
										<Button
											size="sm"
											variant="ghost"
											className="h-8 w-8 p-0"
											onClick={() => copyToClipboard(providerModelName)}
											title="Copy model ID to clipboard"
										>
											{copiedModel === providerModelName ? (
												<Check className="h-4 w-4 text-green-600 dark:text-green-400" />
											) : (
												<Copy className="h-4 w-4" />
											)}
										</Button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Mobile Card View */}
			<div className="md:hidden space-y-4">
				{providers.map((provider) => {
					const providerStability = provider.stability || modelStability;
					const statusBadge = getStatusBadge(providerStability);
					const ProviderIcon = getProviderIcon(provider.providerId);
					const providerModelName = `${provider.providerId}/${modelName}`;

					return (
						<div
							key={`${provider.providerId}-${provider.modelName}-${modelName}`}
							className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800 hover:shadow-md transition-shadow"
						>
							{/* Provider Header */}
							<div className="flex items-start gap-3 mb-4">
								<div className="w-8 h-8 bg-gray-100 dark:bg-slate-700 rounded flex items-center justify-center flex-shrink-0">
									{ProviderIcon ? (
										<ProviderIcon className="h-6 w-6" />
									) : (
										<span className="text-xs font-semibold text-gray-900 dark:text-white">
											{provider.providerInfo?.name?.charAt(0).toUpperCase() ||
												"?"}
										</span>
									)}
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1 flex-wrap">
										<h3 className="font-semibold text-gray-900 dark:text-white">
											{provider.providerInfo?.name || provider.providerId}
										</h3>
										{isCheapest(provider) && (
											<Badge
												variant="outline"
												className="text-xs bg-green-500/10 text-green-600 border-green-500/20 dark:text-green-400 whitespace-nowrap"
											>
												Best
											</Badge>
										)}
									</div>
									<code className="text-xs text-gray-500 dark:text-slate-400 font-mono break-all">
										{providerModelName}
									</code>
								</div>
							</div>

							{/* Pricing and Details */}
							<div className="grid grid-cols-2 gap-4 mb-4">
								<div>
									<div className="text-xs text-gray-500 dark:text-slate-400 mb-1">
										Context
									</div>
									<div className="font-semibold text-gray-900 dark:text-white">
										{provider.contextSize
											? formatContextSize(provider.contextSize)
											: "—"}
									</div>
								</div>
								<div>
									<div className="text-xs text-gray-500 dark:text-slate-400 mb-1">
										Input Price
									</div>
									<div
										className={`font-semibold ${
											isCheapest(provider)
												? "text-green-700 dark:text-green-300"
												: "text-gray-900 dark:text-white"
										}`}
									>
										{isVideo ? (
											videoPerSecLabel(provider)
										) : isMusic ? (
											musicPerSongLabel(provider)
										) : provider.discount ? (
											<div className="flex flex-col">
												<span className="line-through text-gray-400 text-xs">
													{formatPrice(provider.inputPrice)}
												</span>
												<span>
													{formatPrice(
														provider.inputPrice!
															? provider.inputPrice * (1 - provider.discount)
															: undefined,
													)}
												</span>
											</div>
										) : (
											formatPrice(provider.inputPrice)
										)}
									</div>
								</div>
								<div>
									<div className="text-xs text-gray-500 dark:text-slate-400 mb-1">
										{isVideo
											? "Per second"
											: isMusic
												? "Per song"
												: "Output Price"}
									</div>
									<div className="font-semibold text-gray-900 dark:text-white">
										{isVideo
											? "by resolution"
											: isMusic
												? "flat"
												: formatPrice(provider.outputPrice)}
									</div>
								</div>
								<div>
									<div className="text-xs text-gray-500 dark:text-slate-400 mb-1">
										Status
									</div>
									<Badge
										variant="outline"
										className={`text-xs ${statusBadge.className}`}
									>
										{statusBadge.label}
									</Badge>
								</div>
							</div>

							{/* Action Buttons */}
							<div className="flex gap-2 flex-wrap">
								{!isEmbedding && !isAudio && !isMusic && (
									<Button
										asChild
										size="sm"
										className="flex-1 h-8 bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white gap-1"
									>
										<a
											href={`${playgroundUrl}?model=${encodeURIComponent(providerModelName)}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											<Zap className="h-3.5 w-3.5" />
											<span className="text-xs font-medium">Try Now</span>
										</a>
									</Button>
								)}
								<Button
									size="sm"
									variant="ghost"
									className="h-8 w-8 p-0"
									onClick={() => setShowCodeDialog(providerModelName)}
									title="View code examples"
								>
									<Code className="h-4 w-4" />
								</Button>
								<Button
									size="sm"
									variant="ghost"
									className="h-8 w-8 p-0"
									onClick={() => copyToClipboard(providerModelName)}
									title="Copy model ID to clipboard"
								>
									{copiedModel === providerModelName ? (
										<Check className="h-4 w-4 text-green-600 dark:text-green-400" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
						</div>
					);
				})}
			</div>

			{/* Code Examples Dialog */}
			{showCodeDialog && (
				<div
					className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
					onClick={() => setShowCodeDialog(null)}
				>
					<div
						className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="bg-gray-50 dark:bg-slate-800 px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
								Code Examples
							</h3>
							<button
								onClick={() => setShowCodeDialog(null)}
								className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-gray-200 text-2xl"
							>
								✕
							</button>
						</div>

						{/* Tabs */}
						<div className="flex border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-900">
							{[
								{ id: "openai" as const, label: "OpenAI SDK" },
								{ id: "client" as const, label: "client AI SDK" },
								{ id: "curl" as const, label: "cURL" },
							].map((tab) => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
										activeTab === tab.id
											? "border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-400"
											: "border-transparent text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-gray-200"
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>

						{/* Code Content */}
						<div className="flex-1 overflow-y-auto">
							{(() => {
								const examples = getCodeExamples(showCodeDialog);
								const codeMap = {
									openai: examples.openaiSdk,
									client: examples.routerbenchSdk,
									curl: examples.curl,
								};

								return (
									<div className="p-6">
										<div className="flex justify-end mb-3">
											<Button
												size="sm"
												variant="outline"
												className="h-8 gap-2 text-xs"
												onClick={() =>
													copyCodeToClipboard(codeMap[activeTab], activeTab)
												}
											>
												{copiedCode === activeTab ? (
													<>
														<Check className="h-4 w-4 text-green-600" />
														Copied!
													</>
												) : (
													<>
														<Copy className="h-4 w-4" />
														Copy Code
													</>
												)}
											</Button>
										</div>
										<div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-lg border border-slate-700/50 shadow-lg overflow-hidden">
											<pre className="bg-slate-950 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono leading-relaxed">
												<code className="text-slate-200">
													{codeMap[activeTab]}
												</code>
											</pre>
										</div>
									</div>
								);
							})()}
						</div>

						{/* Footer */}
						<div className="bg-gray-50 dark:bg-slate-800 px-6 py-4 border-t border-gray-200 dark:border-slate-700">
							<p className="text-xs text-gray-600 dark:text-slate-400">
								💡 Replace{" "}
								<code className="bg-gray-200 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-800 dark:text-slate-200">
									$ROUTERBENCH_API_KEY
								</code>{" "}
								or{" "}
								<code className="bg-gray-200 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-800 dark:text-slate-200">
									process.env.ROUTERBENCH_API_KEY
								</code>{" "}
								with your actual API key
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
